import { Location } from '@/api/types/Location';
import { LocationContext } from '@/contexts/LocationContext';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  LinearProgress,
  Stack,
  useTheme,
  Dialog,
  IconButton,
  Tooltip,
} from '@mui/material';
import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Close } from '@mui/icons-material';
import ColorSelector from '@/components/notes/colorSelect/ColorSelector';
import { ResponseError } from '@api';
import DeleteLocation from './DeleteLocation';
import LocationInput from './LocationInput';

type ManageLocationDialogProps = {
  location?: Location;
  open: boolean;
  onClose: () => void;
};

type ValidLocation = {
  valid: boolean;
  message?: string;
};

const defaultLocation: Location = {
  locationId: 0,
  locationName: '',
  locationAddress: '',
  color: '#53C433',
};

const ManageLocationsDialog = ({ location, open, onClose }: ManageLocationDialogProps) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { createLocation, editLocation } = useContext(LocationContext);

  const [formLocation, setFormLocation] = useState<Location>(location ?? defaultLocation);
  const [validLocation, setValidLocation] = useState<ValidLocation>({ valid: false });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<boolean | string>(false);

  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const locationNameInputRef = useRef<HTMLInputElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const trapFocus = useCallback(
    (e: KeyboardEvent) => {
      if (!open || !dialogRef.current) return;

      const focusableElements = dialogRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    },
    [open],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', trapFocus);
      return () => {
        document.removeEventListener('keydown', trapFocus);
      };
    }
  }, [open, trapFocus]);

  useEffect(() => {
    if (!open) {
      setFormLocation(defaultLocation);
      setError(false);
    } else {
      setFormLocation(location ?? defaultLocation);

      setTimeout(() => {
        if (locationNameInputRef.current) {
          locationNameInputRef.current.focus();
        }
      }, 100);
    }
  }, [open, location]);

  const onSave = async () => {
    setSending(true);
    let create;
    if (location) {
      create = await editLocation(formLocation);
    } else {
      create = await createLocation(formLocation);
    }

    if (create && create instanceof ResponseError) {
      setError(true);
      setSending(false);

      return;
    }

    setSending(false);
    setFormLocation(formLocation);
    onClose();
  };

  const handleCloseButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    const validLocation =
      ((formLocation.locationName?.length ?? 0) !== 0 ||
        (formLocation.locationAddress?.length ?? 0) !== 0) &&
      (formLocation.color ?? '').match(/^#[0-9A-F]{6}$/i) !== null;

    if (
      location &&
      location.locationName === formLocation.locationName &&
      location.locationAddress === formLocation.locationAddress &&
      location.color === formLocation.color
    ) {
      setValidLocation({ valid: false, message: 'No changes made' });
      return;
    }

    if (!validLocation) {
      if (
        (formLocation.locationName?.length ?? 0) === 0 &&
        (formLocation.locationAddress?.length ?? 0) === 0
      ) {
        setValidLocation({
          valid: false,
          message: 'Location address and name cannot be empty',
        });
      } else {
        setValidLocation({ valid: false, message: 'Invalid location' });
      }
      return;
    }

    setValidLocation({ valid: true });
  }, [formLocation, location]);

  const renderDeleteButton = () => {
    if (!location) return null;

    return (
      <Tooltip title={t('Delete location')} arrow>
        <DeleteLocation
          location={location}
          onClose={onClose}
          ref={deleteButtonRef as React.Ref<HTMLButtonElement>}
        />
      </Tooltip>
    );
  };

  return (
    <Dialog
      ref={dialogRef}
      open={open}
      onClose={onClose}
      aria-labelledby="manage-location-dialog-title"
      aria-describedby="manage-location-dialog-description"
      fullWidth={true}
      maxWidth={'md'}
      onKeyDown={handleKeyDown}
      role="dialog"
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: palette.background.paper,
          px: 2,
        }}
      >
        {renderDeleteButton()}
        <DialogTitle
          id="manage-location-dialog-title"
          sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
        >
          <Input
            value={formLocation.locationName}
            onChange={(e) => setFormLocation({ ...formLocation, locationName: e.target.value })}
            placeholder={t('Location name') ?? ''}
            inputProps={{
              maxLength: 64,
              'aria-label': t('Location name') ?? '',
            }}
            inputRef={locationNameInputRef}
            sx={{
              '& .MuiInputBase-input': {
                padding: 0,
                fontSize: '1.25rem',
                textAlign: 'center',
                color: palette.black,
              },
              ':before': {
                borderBottomColor: palette.black,
              },
              ':after': {
                borderBottomColor: palette.black,
              },
            }}
          />
        </DialogTitle>
        <Tooltip title={t('Close')} arrow>
          <IconButton
            onClick={onClose}
            aria-label={t('Close') as string}
            ref={closeButtonRef}
            onKeyDown={handleCloseButtonKeyDown}
            sx={{
              cursor: 'pointer',
            }}
            edge="end"
          >
            <Close />
          </IconButton>
        </Tooltip>
      </Box>
      <DialogContent
        id="manage-location-dialog-description"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 3, px: 1 }}>
          <LocationInput
            value={
              location?.locationAddress
                ? location.locationAddress
                : formLocation.locationAddress || ''
            }
            onChange={(newAddress) => {
              setFormLocation({ ...formLocation, locationAddress: newAddress });
            }}
            error={error}
            setError={setError}
            isManagingLocations={true}
          />
        </Box>

        {error && (
          <Box
            sx={{
              color: error ? 'red' : 'orange',
              fontSize: '1.25rem',
              fontWeight: 'bold',
              padding: '5px',
              marginBottom: '10px',
            }}
            role="alert"
          >
            {error && t('Something went wrong')}
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '10fr 1fr',
            '@media (max-width: 384px)': {
              gridTemplateColumns: '1fr',
            },
          }}
        >
          <ColorSelector
            selectedColor={formLocation.color}
            onSelectColor={(newColor) => setFormLocation({ ...formLocation, color: newColor })}
          />
          <DialogActions sx={{ justifyContent: 'unset' }}>
            <Button
              onClick={onSave}
              variant="contained"
              color="primary"
              disabled={!!error || !validLocation.valid || sending}
              ref={saveButtonRef}
              aria-label={t('Save location') as string}
              aria-disabled={!!error || !validLocation.valid || sending}
            >
              {t('Save')}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
      {sending && (
        <Stack
          justifyContent="center"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1,
          }}
          role="progressbar"
          aria-label={t('Saving location') as string}
        >
          <LinearProgress color="info" />
        </Stack>
      )}
    </Dialog>
  );
};

export default ManageLocationsDialog;
